import React from "react";
import { tokens, sx } from "./tokens";

// ─────────────────────────────────────────────────────────────────
//  Panel — card surface with optional uppercase header
// ─────────────────────────────────────────────────────────────────
export const Panel = ({ title, action, children, style = {}, bodyStyle = {} }) => (
  <div style={{
    background: tokens.surface.panel,
    border: `1px solid ${tokens.border.soft}`,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...style,
  }}>
    {title && (
      <div style={{
        padding: "12px 16px",
        borderBottom: `1px solid ${tokens.border.soft}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: 12,
          fontWeight: 700,
          color: tokens.text.body,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}>{title}</div>
        {action}
      </div>
    )}
    <div style={bodyStyle}>{children}</div>
  </div>
);

// Card variant with brighter "cardHead" strip (used inside detail right rails)
export const Card = ({ title, action, children, style = {}, bodyStyle = { padding: 14 } }) => (
  <div style={{ ...sx.card, ...style }}>
    {title && (
      <div style={{
        ...sx.cardHead,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}>
        <span>{title}</span>
        {action}
      </div>
    )}
    <div style={bodyStyle}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  PageHead — title + subtitle + right-side actions, with bottom border
// ─────────────────────────────────────────────────────────────────
export const PageHead = ({ title, subtitle, actions }) => (
  <div style={{
    paddingBottom: 14,
    borderBottom: `1px solid ${tokens.border.soft}`,
    marginBottom: 18,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  }}>
    <div>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: tokens.text.primary, letterSpacing: "-0.5px" }}>
        {title}
      </h1>
      {subtitle && (
        <p style={{ margin: "3px 0 0", color: tokens.text.faint, fontSize: 13 }}>{subtitle}</p>
      )}
    </div>
    {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  KpiCard — number + label + optional icon, delta, sub, click
// ─────────────────────────────────────────────────────────────────
export const KpiCard = ({ label, value, icon: Icon, color, sub, onClick, delta, deltaUpColor = tokens.accent.red2, deltaDownColor = tokens.accent.green2 }) => (
  <div onClick={onClick} style={{
    flex: 1,
    minWidth: 160,
    background: tokens.surface.panel,
    border: `1px solid ${tokens.border.soft}`,
    borderRadius: tokens.radius.md,
    padding: "15px 16px",
    position: "relative",
    overflow: "hidden",
    cursor: onClick ? "pointer" : "default",
    boxShadow: tokens.shadow.panel,
    transition: "all 0.18s",
  }}
  onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.borderColor = "rgba(79,111,255,0.25)"; }}}
  onMouseLeave={e => { if (onClick) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = tokens.border.soft; }}}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: tokens.shadow.cardSheen }} />
    {Icon && (
      <div style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", opacity: 0.10 }}>
        <Icon size={36} color={color} />
      </div>
    )}
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -1, lineHeight: 1, color }}>{value ?? "—"}</div>
        {delta != null && delta !== 0 && (
          <span style={{ fontSize: 11, fontWeight: 700,
            color: delta > 0 ? deltaUpColor : deltaDownColor }}>
            {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, color: tokens.text.label, marginTop: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: tokens.text.faint, marginTop: 3 }}>{sub}</div>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  StatusBadge / PriorityBadge / SLABadge — pulsing pills
// ─────────────────────────────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  const map = {
    "Open":        { bg:"rgba(96,165,250,0.15)",  color:"var(--badge-blue)",   border:"1px solid rgba(96,165,250,0.35)",  anim:"badge-blue 4s ease-in-out infinite" },
    "In Progress": { bg:"rgba(234,179,8,0.15)",   color:"var(--badge-amber)",  border:"1px solid rgba(234,179,8,0.35)",   anim:"badge-amber 4s ease-in-out infinite" },
    "On Hold":     { bg:"rgba(249,115,22,0.15)",  color:"var(--badge-orange)", border:"1px solid rgba(249,115,22,0.35)", anim:"badge-orange 4.5s ease-in-out infinite" },
    "Resolved":    { bg:"rgba(52,211,153,0.15)",  color:"var(--badge-green)",  border:"1px solid rgba(52,211,153,0.35)", anim:"badge-green 4.5s ease-in-out infinite" },
    "Closed":      { bg:"rgba(148,163,184,0.12)", color:"var(--badge-gray)",   border:"1px solid rgba(148,163,184,0.25)" },
  };
  const s = map[status] || map["Open"];
  return (
    <span style={{ ...sx.badgeBase, background:s.bg, color:s.color, border:s.border, animation:s.anim, fontWeight:700 }}>
      {status}
    </span>
  );
};

export const PriorityBadge = ({ priority }) => {
  const map = {
    Critical: { bg:"rgba(239,68,68,0.15)",  color:"var(--badge-red)",    border:"1px solid rgba(239,68,68,0.4)",   dot:"#ef4444",
                glow:"0 0 6px rgba(239,68,68,0.12)", anim:"badge-red 3s ease-in-out infinite", dotAnim:"dot-pulse 2.4s ease-in-out infinite" },
    High:     { bg:"rgba(249,115,22,0.15)", color:"var(--badge-orange)", border:"1px solid rgba(249,115,22,0.35)", dot:"#f97316", anim:"badge-orange 4s ease-in-out infinite" },
    Medium:   { bg:"rgba(234,179,8,0.15)",  color:"var(--badge-amber)",  border:"1px solid rgba(234,179,8,0.35)",  dot:"#eab308", anim:"badge-amber 4.5s ease-in-out infinite" },
    Low:      { bg:"rgba(34,197,94,0.12)",  color:"var(--badge-green)",  border:"1px solid rgba(34,197,94,0.3)",   dot:"#22c55e" },
  };
  const s = map[priority] || map.Medium;
  return (
    <span style={{ ...sx.badgeBase, background:s.bg, color:s.color, border:s.border, boxShadow:s.glow, animation:s.anim, fontWeight:700 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:s.dot,
        boxShadow:s.dotAnim ? `0 0 5px ${s.dot}` : "none", flexShrink:0,
        animation:s.dotAnim }}/>
      {priority}
    </span>
  );
};

export const SLABadge = ({ status }) => {
  const map = {
    ok:     { label:"IN SLA",  bg:"rgba(52,211,153,0.13)", color:"var(--badge-green)", border:"1px solid rgba(52,211,153,0.3)",  anim:"badge-green 4.5s ease-in-out infinite" },
    breach: { label:"BREACH",  bg:"rgba(239,68,68,0.18)",  color:"var(--badge-red)",   border:"1px solid rgba(239,68,68,0.45)", shadow:"0 0 8px rgba(239,68,68,0.15)", anim:"badge-red 2.8s ease-in-out infinite" },
    hold:   { label:"ON HOLD", bg:"rgba(234,179,8,0.15)",  color:"var(--badge-pink)",  border:"1px solid rgba(234,179,8,0.35)", anim:"badge-amber 4.5s ease-in-out infinite" },
  };
  const s = map[status] || map.ok;
  return (
    <span style={{ ...sx.badgeBase, background:s.bg, color:s.color, border:s.border, boxShadow:s.shadow, animation:s.anim, fontWeight:700 }}>
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────
//  SelectBubble — circular alternative to <input type=checkbox>
// ─────────────────────────────────────────────────────────────────
export const SelectBubble = ({ checked, indeterminate, onChange, size = 18 }) => (
  <button
    type="button"
    onClick={e => { e.stopPropagation(); onChange?.(); }}
    aria-checked={checked}
    role="checkbox"
    style={{
      width: size, height: size, borderRadius: "50%",
      background: checked
        ? tokens.gradient.primary
        : indeterminate
          ? "rgba(79,111,255,0.18)"
          : tokens.surface.input,
      border: checked
        ? "1px solid rgba(79,111,255,0.6)"
        : indeterminate
          ? "1px solid rgba(79,111,255,0.5)"
          : `1px solid ${tokens.border.strong}`,
      boxShadow: checked
        ? "0 0 10px rgba(79,111,255,0.45), inset 0 1px 0 rgba(255,255,255,0.18)"
        : "none",
      cursor: "pointer", padding: 0, flexShrink: 0,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.18s ease",
    }}
    onMouseEnter={e => { if (!checked) e.currentTarget.style.borderColor = "rgba(79,111,255,0.45)"; }}
    onMouseLeave={e => { if (!checked) e.currentTarget.style.borderColor = indeterminate ? "rgba(79,111,255,0.5)" : tokens.border.strong; }}
  >
    {checked && (
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )}
    {!checked && indeterminate && (
      <span style={{ width: size * 0.45, height: 2, background: "#a5b4fc", borderRadius: 99 }} />
    )}
  </button>
);

// ─────────────────────────────────────────────────────────────────
//  MiniAvatar — colored circular initials chip (stable color from name)
// ─────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  { bg: "linear-gradient(135deg,#312e81,#4f6fff)", color: "#a5b4fc" },
  { bg: "linear-gradient(135deg,#064e3b,#065f46)", color: "#6ee7b7" },
  { bg: "linear-gradient(135deg,#431407,#78350f)", color: "#fdba74" },
  { bg: "linear-gradient(135deg,#1e3a5f,#4f6fff)", color: "#93c5fd" },
  { bg: "linear-gradient(135deg,#581c87,#7e22ce)", color: "#d8b4fe" },
  { bg: "linear-gradient(135deg,#831843,#be185d)", color: "#f9a8d4" },
];

export function avatarFor(name) {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

export function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

export const MiniAvatar = ({ name, size = 22, fontSize, glow = false }) => {
  const av = avatarFor(name);
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: av.bg, color: av.color,
      fontSize: fontSize ?? Math.max(9, Math.round(size * 0.4)),
      fontWeight: 700,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      boxShadow: glow ? "0 0 10px rgba(79,111,255,0.3)" : "none",
    }}>
      {initialsOf(name)}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────
//  Spinner — quick loading spinner
// ─────────────────────────────────────────────────────────────────
export const Spinner = ({ size = 24, color = tokens.accent.indigo }) => (
  <div style={{
    width: size, height: size,
    border: `${Math.max(2, size / 12)}px solid ${color}`,
    borderTopColor: "transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  }} />
);

export const FullPageSpinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: tokens.text.faint }}>
    <Spinner size={28} />
  </div>
);
